'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

import {
   FONT_AWESOME_CSS_CDN_URL,
   LAYERED_MODAL_JS_CDN_URL,
   LAYERED_MODAL_CSS_CDN_URL,
} from '@constants/cdn-urls';

/**
 * Injects third-party stylesheets after the first paint so they do not block LCP/FCP.
 * Font Awesome icons may appear briefly after paint on pages that rely on FA classes alone.
 */
function appendStylesheetOnce(href) {
   if (typeof document === 'undefined') return;
   if (document.querySelector(`link[href="${href}"]`)) return;

   const link = document.createElement('link');
   link.rel = 'stylesheet';
   link.href = href;
   document.head.appendChild(link);
}

function appendScriptOnce(src) {
   if (typeof document === 'undefined') return;
   if (document.querySelector(`script[src="${src}"]`)) return;

   const script = document.createElement('script');
   script.src = src;
   script.defer = true;
   document.body.appendChild(script);
}

function pageContainsFontAwesomeMarkers() {
   if (typeof document === 'undefined') return false;
   return Boolean(
      document.querySelector(
         '.fa, .fas, .far, .fab, .fa-solid, [class*=" fa-"], [class^="fa-"]'
      )
   );
}

function routeLikelyNeedsFontAwesome(pathname) {
   return (
      pathname.startsWith('/quotes/') ||
      pathname.startsWith('/claims/') ||
      pathname.startsWith('/blog') ||
      pathname.startsWith('/news') ||
      pathname.startsWith('/tips') ||
      pathname.startsWith('/contact')
   );
}

function routeLikelyNeedsLayeredModal(pathname) {
   return pathname.startsWith('/quotes/') || pathname.startsWith('/claims/');
}

function pageContainsLayeredModalMarkers() {
   if (typeof document === 'undefined') return false;
   return Boolean(
      document.querySelector(
         '.layered-modal, .simple-modal, [data-layered-modal], [data-simple-modal]'
      )
   );
}

export default function DeferredGlobalStyles() {
   const pathname = usePathname();

   useEffect(() => {
      // Home + lightweight legal/static routes: skip FA/modal injection work entirely.
      if (pathname === '/' || pathname === '/privacy' || pathname === '/terms') return;

      const shouldLoadFontAwesome =
         routeLikelyNeedsFontAwesome(pathname) || pageContainsFontAwesomeMarkers();

      const shouldLoadLayeredModal =
         routeLikelyNeedsLayeredModal(pathname) || pageContainsLayeredModalMarkers();

      if (shouldLoadFontAwesome) {
         appendStylesheetOnce(FONT_AWESOME_CSS_CDN_URL);
      }

      if (shouldLoadLayeredModal) {
         appendStylesheetOnce(LAYERED_MODAL_CSS_CDN_URL);
         appendScriptOnce(LAYERED_MODAL_JS_CDN_URL);
      }
   }, [pathname]);

   return null;
}
